import React from 'react';
import { Autocomplete, Divider, Stack, TextField } from '@mui/material';
import { theme } from '../../../../../../../common/styles/theme';
import { Text } from '../../../../../../../common/components/Text/Text';
import LangFlag from '../../../../../shared/LangFlag/LangFlag';
import { TBookLangOption } from './types';

interface IBookLangSelectProps {
	label: string;
	// endless-book language name
	value: string;
	options: TBookLangOption[];
	onChange: (bookName: string) => void;
}

// Same list as the lesson's language picker: languages of the user's lessons first, then the rest.
export const BookLangSelect: React.FC<IBookLangSelectProps> = ({ label, value, options, onChange }) => {
	// a language our list doesn't have (set in endless-book) stays shown, without a flag
	const all = !value || options.some(o => o.bookName === value)
		? options
		: [{ code: '', name: value, bookName: value, highlight: false, itemsCount: null }, ...options];
	const selected = all.find(o => o.bookName === value) || null;
	const lastGroupCode = [...all].reverse().find(o => o.itemsCount !== null)?.code;

	return (
		// DisableClearable=boolean: value may be null (nothing matched) yet the clear button stays hidden
		<Autocomplete<TBookLangOption, false, boolean, false>
			autoHighlight
			disableClearable
			sx={{ flex: 1, minWidth: 0 }}
			options={all}
			value={selected}
			getOptionLabel={(o) => o.name}
			isOptionEqualToValue={(o, v) => o.bookName === v.bookName}
			onChange={(e, o) => o && onChange(o.bookName)}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					variant={'filled'}
					InputLabelProps={{ shrink: true }}
					InputProps={{
						...params.InputProps,
						startAdornment: selected?.code ? <LangFlag code={selected.code} showCode={false} /> : null,
					}}
				/>
			)}
			renderOption={(props, option) => {
				const { key, ...optionProps } = props as any;
				return (
					<React.Fragment key={key}>
						<li {...optionProps}>
							<Stack direction={'row'} alignItems={'center'} gap={theme.spacing(1)}>
								<LangFlag code={option.code} showCode={false} />
								<Text variant={'subtitle2'}>
									{option.highlight ? <b>{option.name}</b> : option.name}
								</Text>
							</Stack>
						</li>
						{option.code === lastGroupCode && <Divider />}
					</React.Fragment>
				);
			}}
		/>
	);
};
